import { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

interface Account {
  id: number;
  accountName: string;
  holderName: string;
  accountNo: string | null;
  balanceAmount: string;
  accountType: string;
}

const LEDGER_OPTIONS = [
  { value: "LDG_BANK_TO_BANK", label: "Bank to Bank Transfer" },
  { value: "LDG_BANK_TO_CASH", label: "Bank to Cash Transfer" },
  { value: "LDG_CASH_TO_BANK", label: "Cash to Bank Transfer" },
  { value: "LDG_DIRECT_TRANSFER", label: "Direct Transfer" },
];

export default function CashTransferCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
  const [cashAccount, setCashAccount] = useState<Account | null>(null);

  const getAccounts = useCallback(async () => {
    try {
      const response = await request.get("api/getAccounts");
      setBankAccounts(response.data.bankAccounts || []);
      if (response.data.cashAccount) {
        setCashAccount(response.data.cashAccount);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, []);

  useEffect(() => {
    getAccounts();
  }, [getAccounts]);

  const formik = useFormik({
    initialValues: {
      ledgerCode: "",
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      remark: "",
    },
    validationSchema: Yup.object({
      ledgerCode: Yup.string().required("Transaction type is required"),
      fromAccountId: Yup.string().when("ledgerCode", {
        is: (val: string) => val !== "LDG_DIRECT_TRANSFER",
        then: (schema) => schema.required("From account is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      toAccountId: Yup.string().required("To account is required"),
      amount: Yup.number()
        .typeError("Amount must be a number")
        .required("Amount is required")
        .min(0.01, "Amount must be greater than 0"),
      remark: Yup.string().max(255, "Remark must be at most 255 characters"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          ledgerCode: values.ledgerCode,
          fromAccountId: values.fromAccountId || null,
          toAccountId: values.toAccountId,
          amount: Number(values.amount),
          remark: values.remark,
        };
        let response;
        if (id) {
          response = await request.put("api/cashTransfer/" + id, payload);
        } else {
          response = await request.post("api/cashTransfer", payload);
        }
        if (response?.data?.status) {
          toast.success("Cash transaction saved successfully!");
          formik.resetForm();
        } else if (response?.data?.message) {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  const isDirectTransfer =
    formik.values.ledgerCode === "LDG_DIRECT_TRANSFER";

  useEffect(() => {
    formik.setFieldValue("fromAccountId", "", false);
    formik.setFieldValue("toAccountId", "", false);
  }, [formik.values.ledgerCode]);

  const getFromAccounts = (): Account[] => {
    switch (formik.values.ledgerCode) {
      case "LDG_BANK_TO_BANK":
      case "LDG_BANK_TO_CASH":
        return bankAccounts;
      case "LDG_CASH_TO_BANK":
        return cashAccount ? [cashAccount] : [];
      case "LDG_DIRECT_TRANSFER":
        return [];
      default:
        return cashAccount ? [cashAccount, ...bankAccounts] : bankAccounts;
    }
  };

  const getToAccounts = (): Account[] => {
    switch (formik.values.ledgerCode) {
      case "LDG_BANK_TO_BANK":
        return bankAccounts;
      case "LDG_BANK_TO_CASH":
        return cashAccount ? [cashAccount] : [];
      case "LDG_CASH_TO_BANK":
        return bankAccounts;
      case "LDG_DIRECT_TRANSFER":
        return cashAccount ? [cashAccount, ...bankAccounts] : bankAccounts;
      default:
        return cashAccount ? [cashAccount, ...bankAccounts] : bankAccounts;
    }
  };

  const mapToOptions = (accounts: Account[]) =>
    accounts.map((acc) => ({
      value: acc.id,
      label:
        acc.accountName + (acc.holderName ? " - " + acc.holderName : ""),
    }));

  const fromOptions = mapToOptions(getFromAccounts());
  const toOptions = mapToOptions(getToAccounts());

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={
                id && id !== "new"
                  ? "Edit Cash Transfer"
                  : "New Cash Transfer"
              }
              subtitle="Transfer funds between bank and cash accounts"
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/cash-transfer"),
              }}
              primaryButton={{
                label:
                  id && id !== "new"
                    ? "Update Transaction"
                    : "Create Transaction",
                icon: (
                  <span className="material-symbols-outlined">save</span>
                ),
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <SelectField
                    name="ledgerCode"
                    label="Transaction Type"
                    formik={formik}
                    options={LEDGER_OPTIONS}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  height: "1px",
                  background: "rgba(0,0,0,0.08)",
                  margin: "20px 0",
                }}
              />

              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                {!isDirectTransfer && (
                  <Box className="form-field">
                    <SelectField
                      name="fromAccountId"
                      label="From Account"
                      formik={formik}
                      options={fromOptions}
                    />
                  </Box>
                )}
                <Box className="form-field">
                  <SelectField
                    name="toAccountId"
                    label="To Account"
                    formik={formik}
                    options={toOptions}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="amount"
                    label="Amount"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="remark"
                    label="Remark"
                    type="text"
                    formik={formik}
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
